import { Input } from '@/ui/core/input'
import { Rise } from '@/ui/core/motion'
import { HStack, VStack } from '@/ui/core/stack'
import { useRef } from 'react'
import type { TextInput } from 'react-native'
import { View } from 'react-native'
import { StepLayout } from '../StepLayout'
import {
	type AddressState,
	canProceed,
	getAddressState,
	resolveButtonLabel,
} from '../logic/stepState'
import { normalizeState } from '../logic/usStates'
import { PrimaryStepButton } from './PrimaryStepButton'
import type { StepProps } from './types'

/**
 * The address, as five fields the keyboard can be walked through.
 *
 * Each box says what it holds so the system can fill the lot from a contact card, and each one's
 * return key moves to the next rather than dismissing the keyboard, so an address can be typed
 * without reaching back to the screen between lines. The last one submits if the address is
 * complete.
 */
export const AddressStep = ({
	step,
	state,
	onChange,
	onNext,
	onBack,
	onClose,
	progress,
	isSubmitting,
}: StepProps<AddressState>) => {
	const address = getAddressState(state)
	const set = (field: keyof AddressState) => (value: string) =>
		onChange({ ...address, [field]: value })

	const street2 = useRef<TextInput>(null)
	const city = useRef<TextInput>(null)
	const stateField = useRef<TextInput>(null)
	const zip = useRef<TextInput>(null)

	// Shown against the field it is about, so it turns that field's hairline and sits under it,
	// rather than as a loose line at the foot of the whole address.
	const stateHint =
		address.state.trim().length > 0 && normalizeState(address.state) === null
			? 'Example: NY'
			: undefined

	return (
		<StepLayout
			title={step.title}
			description={step.description}
			progress={progress}
			onBack={onBack}
			onClose={onClose}
			cta={
				<PrimaryStepButton
					label={resolveButtonLabel(step, address)}
					onPress={onNext}
					disabled={!canProceed(step, address)}
					isLoading={isSubmitting}
				/>
			}
		>
			{/* Each line of the address arrives after the one above it, the way every list in this
			    app does. */}
			<VStack gap={10}>
				<Rise index={0}>
					<Input
						value={address.street1}
						onChangeText={set('street1')}
						placeholder="Street"
						autoComplete="street-address"
						textContentType="streetAddressLine1"
						returnKeyType="next"
						submitBehavior="submit"
						onSubmitEditing={() => street2.current?.focus()}
					/>
				</Rise>
				<Rise index={1}>
					<Input
						ref={street2}
						value={address.street2}
						onChangeText={set('street2')}
						placeholder="Apt. #"
						textContentType="streetAddressLine2"
						returnKeyType="next"
						submitBehavior="submit"
						onSubmitEditing={() => city.current?.focus()}
					/>
				</Rise>
				<Rise index={2}>
					<Input
						ref={city}
						value={address.city}
						onChangeText={set('city')}
						placeholder="City"
						autoComplete="postal-address-locality"
						textContentType="addressCity"
						returnKeyType="next"
						submitBehavior="submit"
						onSubmitEditing={() => stateField.current?.focus()}
					/>
				</Rise>
				<Rise index={3}>
					<HStack gap={10}>
						<View className="flex-1">
							<Input
								ref={stateField}
								value={address.state}
								onChangeText={set('state')}
								placeholder="State"
								autoCapitalize="characters"
								autoComplete="postal-address-region"
								textContentType="addressState"
								errorText={stateHint}
								returnKeyType="next"
								submitBehavior="submit"
								onSubmitEditing={() => zip.current?.focus()}
							/>
						</View>
						<View className="flex-1">
							<Input
								ref={zip}
								value={address.zip}
								onChangeText={set('zip')}
								placeholder="Zip"
								keyboardType="number-pad"
								inputMode="numeric"
								autoComplete="postal-code"
								textContentType="postalCode"
								maxLength={10}
								returnKeyType="done"
								onSubmitEditing={() => canProceed(step, address) && onNext()}
							/>
						</View>
					</HStack>
				</Rise>
			</VStack>
		</StepLayout>
	)
}
